import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiDownload, FiExternalLink } from "react-icons/fi";
import useAsync from "../../hooks/useAsync";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import { Card, PageHeader, DataTable, PersonCell, Button, SearchInput, ErrorState, MultiSelect } from "../../components/common";
import { searchEvidence, ATTACHMENT_TYPES } from "../../services/evidenceLinkApi";
import { getActiveKras } from "../../services/kraKpiProposalApi";
import { getKpiList } from "../../services/kraKpiApi";
import { downloadEvidence } from "../../services/recordApi";
import { getErrorMessage } from "../../utils/errors";
import { formatDateTime } from "../../utils/format";

const PAGE = 25;
// [UPDATED] PM-18: "Evidence for", KRA and Type are checkbox filters (several values at once).
const ITEM_TYPES = [{ value: "GOAL", label: "Goals" }, { value: "KRA", label: "KRAs" }, { value: "KPI", label: "KPIs" }];

export default function EvidenceSearchPage() {
  const { cycleId } = useCycle();
  const { activeView } = useRole();
  const [filters, setFilters] = useState({ q: "", employeeId: "", itemType: [], kraId: [], attachmentType: [], dateFrom: "", dateTo: "", allCycles: false });
  const [debounced, setDebounced] = useState(filters);
  const [page, setPage] = useState(0);
  useEffect(() => { const t = setTimeout(() => { setDebounced(filters); setPage(0); }, 300); return () => clearTimeout(t); }, [filters]);

  const kras = useAsync(() => getActiveKras(), []);
  // [NEW] PM-7: KPI library (for the parent KRA of a KPI). Not every role may read it; the column then shows "—".
  const kpis = useAsync(() => getKpiList().catch(() => ({ data: [] })), []);
  const kraName = Object.fromEntries((kras.data || []).map((k) => [k.id, k.name]));
  const kpiParent = Object.fromEntries((kpis.data || []).map((k) => [String(k.id), k.parentKraId]));
  const kraOf = (r) => {
    if (r.itemType === "KRA") return r.itemTitle;
    if (r.itemType === "KPI") return kraName[r.kraId] || kraName[kpiParent[r.kpiId]] || "—";
    return "—";
  };
  const result = useAsync(() => searchEvidence({
    ...debounced, cycleId: debounced.allCycles ? undefined : cycleId, limit: PAGE, offset: page * PAGE,
  }), [debounced, cycleId, page]);
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const own = activeView === "employee";

  const open = async (row) => {
    if (row.type === "URL") { window.open(row.url, "_blank", "noopener,noreferrer"); return; }
    try { await downloadEvidence("performance", row.id, row.fileName); } catch (err) { toast.error(getErrorMessage(err)); }
  };

  if (result.error) return <ErrorState message={result.error} onRetry={result.reload} />;
  const total = result.data?.total || 0;

  return (
    <div className="page">
      <PageHeader
        title={own ? "My evidence" : "Evidence search"}
        subtitle={own ? "Files and links you attached as evidence." : "Search evidence files and links you are allowed to see."}
      />
      <div className="filter-bar">
        <SearchInput value={filters.q} onChange={set("q")} placeholder="Title, description, file, link or goal" />
        {!own && <input className="form-control" value={filters.employeeId} onChange={(e) => set("employeeId")(e.target.value.trim())} placeholder="Employee ID" aria-label="Employee ID" />}
        <MultiSelect label="Evidence for" allLabel="Goals, KRAs and KPIs" keepOrder options={ITEM_TYPES} value={filters.itemType} onChange={set("itemType")} />
        <MultiSelect label="KRA" allLabel="Any KRA" options={(kras.data || []).map((k) => ({ value: String(k.id), label: k.name }))} value={filters.kraId} onChange={set("kraId")} />
        <MultiSelect label="Type" allLabel="Any type" keepOrder options={ATTACHMENT_TYPES.filter((t) => t.value)} value={filters.attachmentType} onChange={set("attachmentType")} />
        <input className="form-control" type="date" value={filters.dateFrom} onChange={(e) => set("dateFrom")(e.target.value)} aria-label="Added from" title="Added from" />
        <input className="form-control" type="date" value={filters.dateTo} onChange={(e) => set("dateTo")(e.target.value)} aria-label="Added until" title="Added until" />
        <label className="checkbox"><input type="checkbox" checked={filters.allCycles} onChange={(e) => set("allCycles")(e.target.checked)} /><span>All cycles</span></label>
      </div>
      <Card padded={false}>
        <DataTable
          label="Evidence"
          rowKey="key"
          pageSize={PAGE}
          rows={result.data?.items || []}
          loading={result.loading && !result.data}
          emptyTitle="No evidence found"
          emptyText="Try other filters, or tick All cycles."
          columns={[
            {
              key: "title", label: "Evidence",
              render: (r) => (
                <span className="cell-stack">
                  <strong>{r.title}</strong>
                  <small>{r.type === "URL" ? r.url : `${r.fileName} (${r.mimeType})`}{r.description ? ` — ${r.description}` : ""}</small>
                </span>
              ),
            },
            ...(own ? [] : [{ key: "employee", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={r.employeeId} /> }]),
            // [UPDATED] PM-7: the single "For" column is split into KRA, KPI and Goal (a KPI also shows its KRA).
            { key: "kra", label: "KRA", sortValue: kraOf, render: (r) => (r.itemTitle ? kraOf(r) : <span className="muted">{r.context}</span>) },
            { key: "kpi", label: "KPI", sortValue: (r) => (r.itemType === "KPI" ? r.itemTitle : ""), render: (r) => (r.itemType === "KPI" ? r.itemTitle : "—") },
            { key: "goal", label: "Goal", sortValue: (r) => (r.itemType === "GOAL" ? r.itemTitle : ""), render: (r) => (r.itemType === "GOAL" ? r.itemTitle : "—") },
            { key: "added", label: "Added", render: (r) => <span className="cell-stack"><span>{formatDateTime(r.addedAt)}</span><small>{r.addedBy}</small></span> },
            {
              key: "act", label: "", align: "right",
              render: (r) => (r.type === "URL"
                ? <Button size="sm" variant="ghost" icon={FiExternalLink} onClick={() => open(r)}>Open</Button>
                : <Button size="sm" variant="ghost" icon={FiDownload} disabled={r.scanStatus !== "CLEAN"} title={r.scanStatus === "CLEAN" ? undefined : "Available after an administrator checks the file"} onClick={() => open(r)}>Download</Button>),
            },
          ]}
        />
        {total > PAGE && (
          <div className="table-footer">
            <span>{page * PAGE + 1}–{Math.min(total, (page + 1) * PAGE)} of {total}</span>
            <span className="row-actions">
              <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button size="sm" variant="ghost" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </span>
          </div>
        )}
      </Card>
    </div>
  );
}
